import { Badge } from '@/components/ui/badge'
import { bytes } from '@/lib/format'
import type { GpuSnapshot } from '@/lib/cluster'

interface Props { gpu: GpuSnapshot }

function tempColor(c: number | null): string {
  if (c == null) return 'text-muted-foreground'
  if (c >= 85) return 'text-destructive'
  if (c >= 70) return 'text-amber-400'
  return 'text-green-500'
}

function MiniBar({ pct }: { pct: number }) {
  const clamped = Math.max(0, Math.min(100, pct))
  return (
    <div className="relative h-1.5 w-20 overflow-hidden rounded-full bg-secondary inline-block align-middle">
      <div className="h-full bg-primary" style={{ width: `${clamped}%` }} />
    </div>
  )
}

export function GpuRow({ gpu }: Props) {
  // util_pct can be null on GPUs that don't report it (e.g. the GB10 /
  // Spark family with unified memory returns "[N/A]" from nvidia-smi).
  // We must not coerce that to 0% because the dashboard would imply the
  // GPU is idle when we actually have no data — same logic as the mem
  // cell below.
  const utilPct = gpu.util_pct ?? 0
  const memPct =
    gpu.mem_used != null && gpu.mem_total
      ? (gpu.mem_used / gpu.mem_total) * 100
      : 0

  return (
    <div className="flex items-center justify-between text-xs gap-2 flex-wrap">
      <Badge variant="outline" className="text-[10px]">
        {gpu.gpu_name ?? 'GPU'} #{gpu.gpu_index}
      </Badge>
      <div className="flex items-center gap-1">
        <span className="text-muted-foreground">util</span>
        <MiniBar pct={utilPct} />
        <span>{gpu.util_pct != null ? `${utilPct.toFixed(0)}%` : '—'}</span>
      </div>
      <div className="flex items-center gap-1">
        <span className="text-muted-foreground">mem</span>
        <MiniBar pct={memPct} />
        <span>
          {gpu.mem_used != null ? bytes(gpu.mem_used) : '—'}
          {' / '}
          {gpu.mem_total != null ? bytes(gpu.mem_total) : '—'}
        </span>
      </div>
      <span className={tempColor(gpu.temp_c)}>
        {gpu.temp_c != null ? `${gpu.temp_c.toFixed(0)}°C` : '—'}
      </span>
    </div>
  )
}
